import {
  Component,
  ElementRef,
  afterNextRender,
  computed,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { HoleKind } from '@core/filters/path-filter/placeholders';
import { PLACEHOLDER_PATTERN } from '@core/utils/recipe-placeholder-pattern';

export interface NewParameter {
  name: string;
  label: string;
}

export interface TakenParameter {
  name: string;
  kind: HoleKind;
}

@Component({
  selector: 'app-new-parameter-prompt',
  imports: [TranslatePipe],
  templateUrl: './new-parameter-prompt.component.html',
  styles: ``
})
export class NewParameterPromptComponent {
  kind = input.required<HoleKind>();
  /** Parameters the host already offers; their names cannot be created again. */
  taken = input<readonly TakenParameter[]>([]);
  /** False hides the label field, for hosts that have nowhere to store one. */
  askLabel = input(true);

  created = output<NewParameter>();
  cancelled = output<void>();

  protected readonly name = signal('');
  protected readonly label = signal('');
  /**
   * The parameter menu lists only the parameters of this value's kind, so a name taken by another
   * kind must say so: sent to the menu, the planner would find nothing there.
   */
  protected readonly nameError = computed<'pattern' | 'taken' | 'other_kind' | null>(() => {
    const name = this.name();
    if (!PLACEHOLDER_PATTERN.test(`{{${name}}}`)) return 'pattern';
    const taken = this.taken().find((parameter) => parameter.name === name);
    if (!taken) return null;
    return taken.kind === this.kind() ? 'taken' : 'other_kind';
  });
  protected readonly takenKind = computed(
    () => this.taken().find((parameter) => parameter.name === this.name())?.kind ?? null
  );

  private readonly nameField =
    viewChild.required<ElementRef<HTMLInputElement>>('nameField');

  constructor() {
    afterNextRender(() => this.nameField().nativeElement.focus());
  }

  protected create(): void {
    if (this.nameError() !== null) return;
    const name = this.name();
    const label = this.askLabel() ? this.label().trim() : '';
    this.created.emit({ name, label: label || name });
  }
}
